import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { roleTours } from './role-tours.js';

const PENDING_TOUR_KEY = 'docuswift-tour-pending';

function getRoles() {
    const roles = window?.docuswiftPortal?.current_user_roles;
    const normalized = Array.isArray(roles) ? [...roles] : [];
    if (normalized.includes('company_member') && !normalized.includes('case_manager')) {
        normalized.push('case_manager');
    }
    return normalized;
}

function getStoragePrefix() {
    const base = window?.docuswiftPortal?.tours?.storage_prefix;
    return typeof base === 'string' && base.trim() !== '' ? base : 'docuswift-tour-v1';
}

function isTourCompleted(id) {
    return window.localStorage.getItem(`${getStoragePrefix()}:${id}`) === '1';
}

function markTourCompleted(id) {
    window.localStorage.setItem(`${getStoragePrefix()}:${id}`, '1');
}

function buildSteps(steps = []) {
    return steps.filter((step) => {
        const selector = typeof step.element === 'string' ? step.element : '';
        if (!selector) {
            return false;
        }
        if (document.querySelector(selector)) {
            return true;
        }
        return step.deferUntilVisible === true;
    });
}

function waitForElement(selector, timeout = 8000) {
    return new Promise((resolve) => {
        const existing = document.querySelector(selector);
        if (existing) {
            resolve(existing);
            return;
        }

        let timer = null;
        const observer = new MutationObserver(() => {
            const element = document.querySelector(selector);
            if (!element) {
                return;
            }
            if (timer) {
                clearTimeout(timer);
            }
            observer.disconnect();
            resolve(element);
        });

        observer.observe(document.body, { childList: true, subtree: true });
        timer = setTimeout(() => {
            observer.disconnect();
            resolve(null);
        }, timeout);
    });
}

async function resolveTourSteps(steps = []) {
    const resolved = [];

    for (const step of steps) {
        const selector = typeof step.element === 'string' ? step.element : '';
        if (!selector) {
            continue;
        }

        let element = document.querySelector(selector);
        if (!element && step.deferUntilVisible) {
            element = await waitForElement(selector);
        }

        if (element) {
            resolved.push(step);
        }
    }

    return resolved;
}

function enrichTourSteps(steps = []) {
    const tourState = { expandedFormId: null };

    const enrichedSteps = steps.map((step) => {
        const enriched = {
            ...step,
            popover: step.popover ? { ...step.popover } : undefined,
        };

        if (step.expandFromSelector) {
            enriched.element = () => {
                const formId = tourState.expandedFormId
                    || document.querySelector(step.expandFromSelector)?.getAttribute('data-form-id');
                if (!formId) {
                    return document.querySelector('.ca-expanded-row');
                }
                return document.querySelector(`.ca-expanded-row[data-parent-form="${formId}"]`);
            };

            enriched.popover = {
                ...enriched.popover,
                onPrevClick: (element, currentStep, { driver }) => {
                    collapseExpandedSubmissionRows();
                    tourState.expandedFormId = null;
                    driver.movePrevious();
                },
            };
        }

        if (
            typeof step.element === 'string'
            && typeof step.viewInExpandedSelector === 'string'
            && step.viewInExpandedSelector.trim() !== ''
        ) {
            enriched.element = () => {
                const formId = tourState.expandedFormId
                    || document.querySelector('.ca-expand-btn')?.getAttribute('data-form-id');
                const viewSelector = step.viewInExpandedSelector;
                if (!formId) {
                    return document.querySelector(viewSelector);
                }
                return document.querySelector(
                    `.ca-expanded-row[data-parent-form="${formId}"] ${viewSelector}`
                );
            };
        }

        if (typeof step.detailElementSelector === 'string' && step.detailElementSelector.trim() !== '') {
            enriched.element = () => {
                return document.querySelector(step.detailElementSelector) || document.querySelector(step.element);
            };
        }

        if (typeof step.openDetailsFromSelector === 'string' && step.openDetailsFromSelector.trim() !== '') {
            const detailsSelector = typeof step.detailsPanelSelector === 'string' && step.detailsPanelSelector.trim() !== ''
                ? step.detailsPanelSelector
                : '#cm_request_detail';
            enriched.popover = {
                ...enriched.popover,
                onPrevClick: (element, currentStep, { driver }) => {
                    closeRequestDetailsPanel(detailsSelector);
                    driver.movePrevious();
                },
            };
        }

        return enriched;
    });

    return enrichedSteps.map((step, index) => {
        const nextStep = enrichedSteps[index + 1];
        const shouldExpandForNextStep = !!nextStep?.expandFromSelector;
        const shouldOpenDetailsForNextStep = typeof nextStep?.openDetailsFromSelector === 'string'
            && nextStep.openDetailsFromSelector.trim() !== '';

        if (!shouldExpandForNextStep && !shouldOpenDetailsForNextStep) {
            return step;
        }

        return {
            ...step,
            popover: {
                ...step.popover,
                onNextClick: async (element, currentStep, { driver }) => {
                    if (shouldExpandForNextStep) {
                        const expandBtn = element?.closest?.('.ca-expand-btn') || element;
                        const expandContext = await expandSubmissionRow(expandBtn);
                        if (expandContext) {
                            tourState.expandedFormId = expandContext.formId;
                        }
                    }

                    if (shouldOpenDetailsForNextStep) {
                        const detailsSelector = typeof nextStep.detailsPanelSelector === 'string' && nextStep.detailsPanelSelector.trim() !== ''
                            ? nextStep.detailsPanelSelector
                            : '#cm_request_detail';
                        await openRequestDetailsFromTable({
                            triggerSelector: nextStep.openDetailsFromSelector,
                            detailsSelector,
                            expandedFormId: tourState.expandedFormId,
                        });
                    }
                    driver.moveNext();
                },
            },
        };
    });
}

function isElementVisible(element) {
    if (!element) {
        return false;
    }
    if (element.style.display === 'none') {
        return false;
    }
    return getComputedStyle(element).display !== 'none';
}

function waitForExpandedRowVisible(row, timeout = 3000) {
    return new Promise((resolve) => {
        if (isElementVisible(row)) {
            resolve(row);
            return;
        }

        let timer = null;
        const observer = new MutationObserver(() => {
            if (!isElementVisible(row)) {
                return;
            }
            if (timer) {
                clearTimeout(timer);
            }
            observer.disconnect();
            resolve(row);
        });

        observer.observe(row, { attributes: true, attributeFilter: ['style', 'class'] });
        timer = setTimeout(() => {
            observer.disconnect();
            resolve(isElementVisible(row) ? row : null);
        }, timeout);
    });
}

function setSubmissionRowExpanded(expandBtn, expanded) {
    const tbody = expandBtn?.closest?.('tbody');
    if (!tbody || !expandBtn) {
        return null;
    }

    const formId = expandBtn.getAttribute('data-form-id');
    if (!formId) {
        return null;
    }

    const formRow = tbody.querySelector(`.ca-form-row[data-form-id="${formId}"]`);
    const expandedRow = tbody.querySelector(`.ca-expanded-row[data-parent-form="${formId}"]`);
    if (!expandedRow) {
        return null;
    }

    if (expanded) {
        expandBtn.setAttribute('aria-expanded', 'true');
        expandBtn.textContent = 'Hide requests';
        formRow?.classList.add('ca-form-row--expanded');
        expandedRow.style.display = '';
    } else {
        expandBtn.setAttribute('aria-expanded', 'false');
        expandBtn.textContent = 'List of requests';
        formRow?.classList.remove('ca-form-row--expanded');
        expandedRow.style.display = 'none';
    }

    return { expandBtn, expandedRow, formId };
}

async function expandSubmissionRow(expandTarget) {
    const expandBtn = typeof expandTarget === 'string'
        ? document.querySelector(expandTarget)
        : expandTarget;

    if (!expandBtn) {
        return null;
    }

    if (expandBtn.getAttribute('aria-expanded') === 'true') {
        const formId = expandBtn.getAttribute('data-form-id');
        const expandedRow = document.querySelector(`.ca-expanded-row[data-parent-form="${formId}"]`);
        if (expandedRow && isElementVisible(expandedRow)) {
            return { expandBtn, expandedRow, formId };
        }
    }

    const expandContext = setSubmissionRowExpanded(expandBtn, true);
    if (!expandContext) {
        return null;
    }

    const visibleRow = await waitForExpandedRowVisible(expandContext.expandedRow);
    if (!visibleRow) {
        return null;
    }

    return { ...expandContext, expandedRow: visibleRow };
}

function collapseExpandedSubmissionRows() {
    document.querySelectorAll('.ca-expand-btn[aria-expanded="true"]').forEach((expandBtn) => {
        setSubmissionRowExpanded(expandBtn, false);
    });
}

function waitForRequestDetailsOpen(detailsSelector, timeout = 8000) {
    return new Promise((resolve) => {
        const panel = document.querySelector(detailsSelector);
        if (!panel) {
            resolve(null);
            return;
        }

        const isOpen = () => panel.classList.contains('active')
            && !!panel.querySelector('.request_details_wrapper');

        if (isOpen()) {
            resolve(panel);
            return;
        }

        let timer = null;
        const observer = new MutationObserver(() => {
            if (!isOpen()) {
                return;
            }
            if (timer) {
                clearTimeout(timer);
            }
            observer.disconnect();
            resolve(panel);
        });

        observer.observe(panel, { attributes: true, childList: true, subtree: true, attributeFilter: ['class'] });
        timer = setTimeout(() => {
            observer.disconnect();
            resolve(isOpen() ? panel : null);
        }, timeout);
    });
}

async function openRequestDetailsFromTable({ triggerSelector, detailsSelector, expandedFormId }) {
    let trigger = null;
    if (expandedFormId) {
        trigger = document.querySelector(`.ca-expanded-row[data-parent-form="${expandedFormId}"] ${triggerSelector}`);
    }
    if (!trigger) {
        trigger = document.querySelector(triggerSelector);
    }
    if (!trigger) {
        return null;
    }

    trigger.click();
    return waitForRequestDetailsOpen(detailsSelector);
}

function closeRequestDetailsPanel(detailsSelector) {
    const panel = document.querySelector(detailsSelector);
    if (!panel || !panel.classList.contains('active')) {
        return;
    }

    const closeBtn = panel.querySelector('.close_request_detail');
    if (closeBtn) {
        closeBtn.click();
        return;
    }

    panel.classList.remove('active');
    const overlayMap = {
        '#cm_request_detail': '.cm-request-detail-overlay',
        '#company_admin_request_detail': '.company-admin-request-detail-overlay',
        '#request_detail': '.request_detail_overlay',
    };
    const overlay = document.querySelector(overlayMap[detailsSelector] || '');
    overlay?.classList.remove('active');
    document.body.style.overflow = '';
}

function closeTourRequestDetailsPanels() {
    ['#cm_request_detail', '#company_admin_request_detail', '#request_detail'].forEach(closeRequestDetailsPanel);
}

const CASE_MANAGER_TOUR_MODALS = [
    '#add_case_manager_modal',
    '#edit_case_manager_modal',
    '#delete_member_modal',
];

function setModalVisible(selector, visible) {
    const modal = document.querySelector(selector);
    if (!modal) {
        return;
    }
    if (visible) {
        modal.style.display = 'block';
        modal.style.opacity = '1';
        return;
    }
    modal.style.opacity = '0';
    modal.style.display = 'none';
}

function closeCaseManagerTourModals() {
    CASE_MANAGER_TOUR_MODALS.forEach((selector) => setModalVisible(selector, false));
}

function syncTourStepModal(step, activeModalSelector) {
    if (step?.closeModal && activeModalSelector) {
        setModalVisible(activeModalSelector, false);
        return '';
    }

    const openModal = typeof step?.openModal === 'string' ? step.openModal : '';
    if (openModal) {
        if (activeModalSelector && activeModalSelector !== openModal) {
            setModalVisible(activeModalSelector, false);
        }
        setModalVisible(openModal, true);
        return openModal;
    }

    const keepModal = typeof step?.keepModal === 'string' ? step.keepModal : '';
    if (keepModal) {
        setModalVisible(keepModal, true);
        return keepModal;
    }

    if (activeModalSelector) {
        setModalVisible(activeModalSelector, false);
    }
    return '';
}

function getAvailableTours() {
    const roles = getRoles();
    const tours = [];

    roles.forEach((role) => {
        const roleEntries = roleTours[role];
        if (!Array.isArray(roleEntries)) {
            return;
        }
        roleEntries.forEach((tour) => {
            if (typeof tour?.match === 'function' && !tour.match()) {
                return;
            }
            if (tour?.launch?.type === 'redirect') {
                tours.push({ ...tour });
                return;
            }
            const steps = buildSteps(tour.steps);
            if (steps.length < 2) {
                return;
            }
            tours.push({ ...tour, steps });
        });
    });

    return tours;
}

async function runTour(tour) {
    let activeModalSelector = '';

    closeCaseManagerTourModals();

    const steps = enrichTourSteps(await resolveTourSteps(tour.steps));
    if (steps.length < 2) {
        return;
    }

    const tourDriver = driver({
        showProgress: true,
        animate: true,
        allowClose: true,
        doneBtnText: 'Done',
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        onHighlightStarted: (_element, step) => {
            activeModalSelector = syncTourStepModal(step, activeModalSelector);
        },
        onDestroyed: () => {
            if (activeModalSelector) {
                setModalVisible(activeModalSelector, false);
            }
            closeCaseManagerTourModals();
            collapseExpandedSubmissionRows();
            closeTourRequestDetailsPanels();
            markTourCompleted(tour.id);
        },
        steps,
    });
    tourDriver.drive();
}

function handleLaunchAction(tour) {
    const launch = tour?.launch;
    if (!launch || launch.type !== 'redirect') {
        return false;
    }

    const targetUrl = typeof launch.to === 'function' ? launch.to() : launch.to;
    const pendingTourId = typeof launch.tourId === 'string' && launch.tourId.trim() !== '' ? launch.tourId : '';
    if (pendingTourId) {
        window.sessionStorage.setItem(PENDING_TOUR_KEY, pendingTourId);
    }
    if (typeof targetUrl === 'string' && targetUrl.trim() !== '') {
        window.location.href = targetUrl;
    }
    return true;
}

function renderQuickTourLauncher(tours, startTour) {
    const existing = document.querySelector('.ds-tour-launcher');
    if (existing) {
        existing.remove();
    }

    const launcher = document.createElement('div');
    launcher.className = 'ds-tour-launcher';
    launcher.innerHTML = `
        <button type="button" class="ds-tour-launcher__toggle" aria-expanded="false">
            Portal Quick Tour
        </button>
        <div class="ds-tour-launcher__panel" hidden>
            <div class="ds-tour-launcher__head">
                <strong>Available Tours</strong>
                <button type="button" class="ds-tour-launcher__close" aria-label="Close">&times;</button>
            </div>
            <div class="ds-tour-launcher__list"></div>
        </div>
    `;

    const toggleButton = launcher.querySelector('.ds-tour-launcher__toggle');
    const closeButton = launcher.querySelector('.ds-tour-launcher__close');
    const panel = launcher.querySelector('.ds-tour-launcher__panel');
    const list = launcher.querySelector('.ds-tour-launcher__list');

    const openPanel = () => {
        panel.hidden = false;
        toggleButton.setAttribute('aria-expanded', 'true');
    };

    const closePanel = () => {
        panel.hidden = true;
        toggleButton.setAttribute('aria-expanded', 'false');
    };

    if (!tours.length) {
        const empty = document.createElement('p');
        empty.className = 'ds-tour-launcher__empty';
        empty.textContent = 'No tours available on this page.';
        list.appendChild(empty);
    } else {
        tours.forEach((tour) => {
            const itemButton = document.createElement('button');
            itemButton.type = 'button';
            itemButton.className = 'ds-tour-launcher__item';
            itemButton.innerHTML = `
                <span class="ds-tour-launcher__item-title">${tour.title || tour.id}</span>
                <span class="ds-tour-launcher__item-meta">${isTourCompleted(tour.id) ? 'Viewed' : 'New'}</span>
            `;
            itemButton.addEventListener('click', () => {
                closePanel();
                startTour(tour.id);
            });
            list.appendChild(itemButton);
        });
    }

    toggleButton.addEventListener('click', () => {
        if (panel.hidden) {
            openPanel();
        } else {
            closePanel();
        }
    });
    closeButton.addEventListener('click', closePanel);

    document.addEventListener('click', (event) => {
        if (!launcher.contains(event.target)) {
            closePanel();
        }
    });
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            closePanel();
        }
    });

    document.body.appendChild(launcher);
}

export function initRoleTours() {
    let autoTourStarted = false;
    let startTour = null;

    const createStartTour = () => async (id) => {
        const tours = getAvailableTours();
        const tour = tours.find((item) => item.id === id);
        if (!tour) {
            return false;
        }
        if (tour.launch) {
            return handleLaunchAction(tour);
        }
        await runTour(tour);
        return true;
    };

    const refreshLauncher = () => {
        const tours = getAvailableTours();
        if (startTour) {
            renderQuickTourLauncher(tours, startTour);
        }
        return tours;
    };

    const maybeStartPendingOrAutoTour = async () => {
        const tours = getAvailableTours();
        if (!tours.length || !startTour) {
            return;
        }

        const pendingTourId = window.sessionStorage.getItem(PENDING_TOUR_KEY) || '';
        if (pendingTourId) {
            const pendingTour = tours.find((tour) => tour.id === pendingTourId);
            if (pendingTour && !pendingTour.launch) {
                window.sessionStorage.removeItem(PENDING_TOUR_KEY);
                autoTourStarted = true;
                await startTour(pendingTour.id);
                return;
            }
        }

        if (autoTourStarted) {
            return;
        }

        const firstAutoTour = tours.find((tour) => !tour.launch && tour.auto && !isTourCompleted(tour.id));
        if (!firstAutoTour) {
            return;
        }

        const waitingForTable = firstAutoTour.steps.some(
            (step) => step.deferUntilVisible && !document.querySelector(step.element)
        );
        if (waitingForTable) {
            return;
        }

        autoTourStarted = true;
        await startTour(firstAutoTour.id);
    };

    startTour = createStartTour();

    const initialTours = getAvailableTours();
    if (!initialTours.length) {
        return;
    }

    document.addEventListener('docuswift:requests-table-ready', () => {
        refreshLauncher();
        maybeStartPendingOrAutoTour();
    });

    window.DocuswiftTours = {
        list: () => getAvailableTours().map((tour) => ({ id: tour.id, title: tour.title || tour.id, auto: !!tour.auto })),
        start: (id) => startTour(id),
        refresh: refreshLauncher,
        reset: (id) => {
            if (typeof id === 'string' && id.trim() !== '') {
                window.localStorage.removeItem(`${getStoragePrefix()}:${id}`);
                return;
            }
            getAvailableTours().forEach((tour) => window.localStorage.removeItem(`${getStoragePrefix()}:${tour.id}`));
        },
    };

    renderQuickTourLauncher(initialTours, startTour);
    maybeStartPendingOrAutoTour();
}

